import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatNumber, formatDecimal } from '@/components/sizing/advancedInsightsShared';
import type { SizingNarrativeData } from '@/types/narrative';
import { Gauge } from 'lucide-react';

type ThroughputUtilizationChartProps = {
  data: SizingNarrativeData;
};

export const ThroughputUtilizationChart: React.FC<ThroughputUtilizationChartProps> = ({ data }) => {
  const { throughput, finalPlan } = data;

  // Calculate utilization percentages
  const promptUtilization = (throughput.gpusForInput / finalPlan.finalGpuCount) * 100;
  const decodeUtilization = (throughput.gpusForOutput / finalPlan.finalGpuCount) * 100;

  // Calculate capacity TPS
  const promptCapacityTps = throughput.inputTpsPerGpu * finalPlan.finalGpuCount;
  const decodeCapacityTps = throughput.outputTpsPerGpu * finalPlan.finalGpuCount;

  // Color coding based on utilization
  const getUtilizationColor = (util: number) => {
    if (util >= 90) return 'hsl(0 84% 60%)'; // red/destructive
    if (util >= 70) return 'hsl(38 92% 50%)'; // amber/warning
    return 'hsl(142 71% 45%)'; // green/success
  };

  const getUtilizationBgColor = (util: number) => {
    if (util >= 90) return 'hsl(0 84% 60% / 0.1)';
    if (util >= 70) return 'hsl(38 92% 50% / 0.1)';
    return 'hsl(142 71% 45% / 0.1)';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-5 w-5 text-primary" />
          Throughput Utilization
        </CardTitle>
        <CardDescription>Token processing capacity and demand</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Prompt Throughput */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Prompt Throughput</span>
            <span 
              className="text-sm font-bold" 
              style={{ color: getUtilizationColor(promptUtilization) }}
            >
              {formatDecimal(promptUtilization, 1)}%
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-8 w-full rounded-lg border border-border overflow-hidden" style={{ backgroundColor: getUtilizationBgColor(promptUtilization) }}>
            <div
              className="h-full transition-all duration-500 flex items-center justify-center text-xs font-medium text-white"
              style={{
                width: `${Math.min(100, promptUtilization)}%`,
                backgroundColor: getUtilizationColor(promptUtilization),
              }}
            >
              {promptUtilization > 15 && `${formatDecimal(promptUtilization, 1)}%`}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="space-y-0.5">
              <p className="text-muted-foreground">Required TPS</p>
              <p className="font-semibold text-foreground">{formatNumber(throughput.inputTpsRequired)}</p>
            </div>
            <div className="space-y-0.5">
              <p className="text-muted-foreground">Capacity TPS</p>
              <p className="font-semibold text-foreground">{formatNumber(promptCapacityTps)}</p>
            </div>
            <div className="space-y-0.5">
              <p className="text-muted-foreground">GPUs Needed</p>
              <p className="font-semibold text-foreground">{formatNumber(Math.ceil(throughput.gpusForInput))}</p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Decode Throughput */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Decode Throughput</span>
            <span 
              className="text-sm font-bold" 
              style={{ color: getUtilizationColor(decodeUtilization) }}
            >
              {formatDecimal(decodeUtilization, 1)}%
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-8 w-full rounded-lg border border-border overflow-hidden" style={{ backgroundColor: getUtilizationBgColor(decodeUtilization) }}>
            <div
              className="h-full transition-all duration-500 flex items-center justify-center text-xs font-medium text-white"
              style={{
                width: `${Math.min(100, decodeUtilization)}%`,
                backgroundColor: getUtilizationColor(decodeUtilization),
              }}
            >
              {decodeUtilization > 15 && `${formatDecimal(decodeUtilization, 1)}%`}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="space-y-0.5">
              <p className="text-muted-foreground">Required TPS</p>
              <p className="font-semibold text-foreground">{formatNumber(throughput.outputTpsRequired)}</p>
            </div>
            <div className="space-y-0.5">
              <p className="text-muted-foreground">Capacity TPS</p>
              <p className="font-semibold text-foreground">{formatNumber(decodeCapacityTps)}</p>
            </div>
            <div className="space-y-0.5">
              <p className="text-muted-foreground">GPUs Needed</p>
              <p className="font-semibold text-foreground">{formatNumber(Math.ceil(throughput.gpusForOutput))}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
